import * as React from "react";
import { useState, useEffect } from "react";
import api from "../../api/api";
import { getErrorMessage } from "../../api/apiError";
import { ErrorMessages } from "../../constants/messages";
import ConfirmationForm from "./ConfirmationForm";
import TemplateTabView from "./TemplateTab/TemplateTabView";
import {
  generateTemplateWorkbook,
  saveTemplateToServer,
  writeWorkbookTemplateId,
  readWorkbookState,
  getActiveVisibleSheetState,
  watchActiveVisibleSheetState,
  type TemplateDataReferenceMode,
  type WorkbookStateResult,
} from "./TemplateWorkbookService";
import {
  handleUdfCategorySelect,
  handleUdfItemSelect,
  handleMasterUdfSelect,
  type UDFCategory,
} from "./ReportServices";
import {
  mergeApiAndColumnFilters,
  buildTemplateWorkbookConfig,
  type EntityTypeItem,
  type FilterDefinition,
  type FilterRow,
  type ColumnDefinition,
  type ColumnRow,
} from "./TemplateServices";
import {
  fetchTemplateColumnDefinitions,
  fetchTemplateEntityNames,
  fetchTemplateFilterDefinitions,
  fetchTemplateRoles,
  fetchTemplateUdfCategories,
  type TemplateColumnLoadResult,
} from "./TemplateTab/TemplateTabApiService";
import {
  mapColumnDefinitionToSelectedRow,
  mapFilterDefinitionToSelectedRow,
  reorderRows,
  resolveWorkbookColumns,
  resolveWorkbookFilters,
} from "./TemplateTab/TemplateTabMapping";
import type { RoleItem } from "./TemplateTab/TemplateTabViewTypes";

interface TemplateTabProps {
  authToken: string;
  identityProvider: string;
  onSaveSuccess?: () => void;
}

const CONFIGURATION_LOAD_TIMEOUT_MS = 20000;

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, timeoutMessage: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs);
    promise.then(
      (value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      }
    );
  });
}

// ── Component ────────────────────────────────────────────────────────────────

const TemplateTab: React.FC<TemplateTabProps> = ({ authToken: _authToken, identityProvider: _identityProvider, onSaveSuccess }) => {
  // ── State for API data ────────────────────────────────────────────────────
  const [entityNames, setEntityNames] = useState<EntityTypeItem[]>([]);
  const [loadingEntityNames, setLoadingEntityNames] = useState<boolean>(false);
  const [loadingColumns, setLoadingColumns] = useState<boolean>(false);
  const [loadingFilters, setLoadingFilters] = useState<boolean>(false);
  const [roles, setRoles] = useState<RoleItem[]>([]);
  const [loadingRoles, setLoadingRoles] = useState<boolean>(false);
  const [loadingUdfs, setLoadingUdfs] = useState<boolean>(false);
  const [isLoadingConfiguration, setIsLoadingConfiguration] = useState<boolean>(false);

  const [selectedEntityType, setSelectedEntityType] = useState<string>("");
  const [entityTypeQuery, setEntityTypeQuery] = useState<string>("");
  const [availableFilters, setAvailableFilters] = useState<FilterDefinition[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<FilterRow[]>([]);
  const [availableColumns, setAvailableColumns] = useState<ColumnDefinition[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<ColumnRow[]>([]);
  const [udfCategories, setUdfCategories] = useState<UDFCategory[]>([]);
  const [selectedUdfs, setSelectedUdfs] = useState<string>("");
  const [templateName, setTemplateName] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [hasGeneratedWorkbook, setHasGeneratedWorkbook] = useState<boolean>(false);
  const [operationError, setOperationError] = useState<string>("");
  const [operationNotice, setOperationNotice] = useState<string>("");
  const [includeUdfs, setIncludeUdfs] = useState<boolean>(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [showFilterPicker, setShowFilterPicker] = useState<boolean>(false);
  const [showColumnPicker, setShowColumnPicker] = useState<boolean>(false);
  const [workbookDetected, setWorkbookDetected] = useState<boolean>(false);
  const [isWorkbookBannerDismissed, setIsWorkbookBannerDismissed] = useState<boolean>(false);
  const [activeEmptyVisibleSheetName, setActiveEmptyVisibleSheetName] = useState<string>("");
  const [dataReferenceMode, setDataReferenceMode] = useState<TemplateDataReferenceMode>("standardTable");
  const [existingTemplateId, setExistingTemplateId] = useState<string>("");
  const [isSaveModeDialogOpen, setIsSaveModeDialogOpen] = useState<boolean>(false);
  const isConfigurationLocked = isLoadingConfiguration;

  const getCurrentTemplateId = () => (existingTemplateId || api.templateId || "").trim();

  const clearCurrentTemplateId = async () => {
    setExistingTemplateId("");
    api.setTemplateId("");
    try {
      await writeWorkbookTemplateId("");
    } catch (error) {
      console.error("Failed to clear workbook template id:", error);
    }
  };

  const handleTemplateNameChange = (value: string) => {
    setTemplateName(value);
    setHasGeneratedWorkbook(false);
    if (getCurrentTemplateId()) {
      void clearCurrentTemplateId();
    }
  };

  const refreshActiveVisibleSheetState = async () => {
    const sheetState = await getActiveVisibleSheetState();
    if (sheetState?.isEmpty) {
      setActiveEmptyVisibleSheetName((previousSheetName) => {
        if (previousSheetName !== sheetState.sheetName) {
          setDataReferenceMode("standardTable");
        }
        return sheetState.sheetName;
      });
      return;
    }
    setActiveEmptyVisibleSheetName("");
    setDataReferenceMode("standardTable");
  };

  const fetchFiltersForEntityRaw = async (entityCode: string): Promise<FilterDefinition[]> => {
    setLoadingFilters(true);
    try {
      const filters = await withTimeout(
        fetchTemplateFilterDefinitions(entityCode, _authToken, _identityProvider),
        CONFIGURATION_LOAD_TIMEOUT_MS,
        "Timed out loading filters."
      );
      setAvailableFilters((previousFilters) => {
        const generatedFilters = previousFilters.filter((filter) => filter.generatedFromColumn);
        return mergeApiAndColumnFilters(filters, generatedFilters);
      });
      return filters;
    } catch (error) {
      console.error("Error fetching filters:", error);
      setOperationError(getErrorMessage(error, ErrorMessages.LoadFiltersFailed));
      setAvailableFilters([]);
      return [];
    } finally {
      setLoadingFilters(false);
    }
  };

  const fetchFiltersForEntity = async (entityCode: string) => {
    const filters = await fetchFiltersForEntityRaw(entityCode);
    setSelectedFilters(filters.map((filter) => mapFilterDefinitionToSelectedRow(filter)));
  };

  // ── Apply a detected workbook state to the form ──────────────────────────
  const applyWorkbookState = async (wb: WorkbookStateResult, entities: EntityTypeItem[]) => {
    const entity =
      entities.find((e) => e.key === wb.entityType) ??
      entities.find((e) => e.dataEndpoint === wb.entityDataEndpoint);
    if (!entity) return;

    setTemplateName(wb.templateName);
    setSelectedEntityType(entity.key);
    setExistingTemplateId(wb.templateId);
    api.setTemplateId(wb.templateId);
    setHasGeneratedWorkbook(false);
    setShowFilterPicker(false);
    setShowColumnPicker(false);

    // Fetch columns from API (skip auto-select; we'll apply workbook columns)
    const fetchedColumns = await fetchColumnsForEntityRaw(entity.key);
    const apiColumns = fetchedColumns.columns;

    setSelectedColumns(wb.columns.length > 0 ? resolveWorkbookColumns(wb.columns, apiColumns) : []);

    const apiFilters = await fetchFiltersForEntityRaw(entity.key);
    const allFilterDefs = mergeApiAndColumnFilters(apiFilters, fetchedColumns.generatedFilters);

    setSelectedFilters(wb.filters.length > 0 ? resolveWorkbookFilters(wb.filters, allFilterDefs) : []);

    // Apply UDFs if present
    if (wb.hasUdfs) {
      setIncludeUdfs(true);
      setSelectedUdfs(wb.udfs);
    } else {
      setIncludeUdfs(false);
      setSelectedUdfs("");
    }

    void fetchUdfsForEntity(entity.key);

    setWorkbookDetected(true);
    setIsWorkbookBannerDismissed(false);
  };

  // ── Fetch entity names on component mount ────────────────────────────────
  useEffect(() => {
    // Do not fetch without a usable token, otherwise an empty-token mount flashes
    // LoadEntityTypesFailed before the real token arrives.
    if (!_authToken) return;
    const fetchEntityNames = async () => {
      setLoadingEntityNames(true);
      try {
        const entities = await fetchTemplateEntityNames(_authToken, _identityProvider);
        setEntityNames(entities);

        // Try to detect and pre-populate from the current workbook
        setIsLoadingConfiguration(true);
        try {
          const wb = await withTimeout(
            readWorkbookState(),
            CONFIGURATION_LOAD_TIMEOUT_MS,
            "Timed out reading workbook configuration."
          );
          if (wb && entities.length > 0) {
            await withTimeout(
              applyWorkbookState(wb, entities),
              CONFIGURATION_LOAD_TIMEOUT_MS,
              "Timed out applying workbook configuration."
            );
          }
        } catch (error) {
          console.error("Error loading workbook configuration:", error);
          setOperationError(getErrorMessage(error, ErrorMessages.LoadWorkbookConfigFailed));
        } finally {
          setIsLoadingConfiguration(false);
        }
      } catch (error) {
        console.error("Error fetching entity names:", error);
        setOperationError(getErrorMessage(error, ErrorMessages.LoadEntityTypesFailed));
      } finally {
        setLoadingEntityNames(false);
      }
    };

    fetchEntityNames();
  }, [_authToken, _identityProvider]);

  useEffect(() => {
    if (!_authToken) return;
    const fetchRoles = async () => {
      setLoadingRoles(true);
      try {
        setRoles(await fetchTemplateRoles(_authToken, _identityProvider));
      } catch (error) {
        console.error("Error fetching roles:", error);
        setOperationError(getErrorMessage(error, ErrorMessages.LoadRolesFailed));
        setRoles([]);
      } finally {
        setLoadingRoles(false);
      }
    };

    fetchRoles();
  }, [_authToken, _identityProvider]);

  useEffect(() => {
    refreshActiveVisibleSheetState();
  }, [selectedEntityType]);

  useEffect(() => {
    let isDisposed = false;
    let unregisterWatcher: (() => Promise<void>) | null = null;

    const handleWorksheetStateChange = async () => {
      if (isDisposed) return;
      await refreshActiveVisibleSheetState();
    };

    const registerWorksheetHandlers = async () => {
      try {
        unregisterWatcher = await watchActiveVisibleSheetState(handleWorksheetStateChange);
      } catch (error) {
        console.error("Failed to register worksheet handlers:", error);
      }
    };

    registerWorksheetHandlers();

    return () => {
      isDisposed = true;

      const removeHandlers = async () => {
        try {
          await unregisterWatcher?.();
        } catch (error) {
          console.error("Failed to remove worksheet handlers:", error);
        }
      };

      void removeHandlers();
    };
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleEntityTypeChange = (value: string) => {
    setSelectedEntityType(value);
    setHasGeneratedWorkbook(false);
    setShowFilterPicker(false);
    setShowColumnPicker(false);
    setIncludeUdfs(false);
    setSelectedRoles([]);
    if (value) {
      // Fetch columns from API for the selected entity
      fetchColumnsForEntity(value);

      // Fetch filters from API for the selected entity
      fetchFiltersForEntity(value);
      
      // Fetch UDFs from API for the selected entity
      fetchUdfsForEntity(value);

      setSelectedUdfs("");
      // Columns will be set after API call completes
      setSelectedColumns([]);
    } else {
      setAvailableFilters([]);
      setAvailableColumns([]);
      setUdfCategories([]);
      setSelectedUdfs("");
      setSelectedFilters([]);
      setSelectedColumns([]);
    }
  };

  // Fetches columns, sets availableColumns, and returns the list (no auto-select)
  const fetchColumnsForEntityRaw = async (
    entityCode: string
  ): Promise<TemplateColumnLoadResult> => {
    setLoadingColumns(true);
    try {
      const result = await withTimeout(
        fetchTemplateColumnDefinitions(entityCode, _authToken, _identityProvider),
        CONFIGURATION_LOAD_TIMEOUT_MS,
        "Timed out loading columns."
      );
      setAvailableColumns(result.columns);
      setAvailableFilters((previousFilters) => {
        const apiFilters = previousFilters.filter((filter) => !filter.generatedFromColumn);
        return mergeApiAndColumnFilters(apiFilters, result.generatedFilters);
      });

      return result;
    } catch (error) {
      console.error("Error fetching columns:", error);
      setOperationError(getErrorMessage(error, ErrorMessages.LoadColumnsFailed));
      setAvailableColumns([]);
      return { columns: [], generatedFilters: [] };
    } finally {
      setLoadingColumns(false);
    }
  };

  // Fetches columns and auto-selects those marked as included (used when user picks an entity)
  const fetchColumnsForEntity = async (entityCode: string) => {
    const fetched = await fetchColumnsForEntityRaw(entityCode);
    const toSelect = fetched.columns.filter((c) => c.defaultSelected);
    setSelectedColumns(toSelect.map(mapColumnDefinitionToSelectedRow));
  };

  // Fetches UDFs for the selected entity
  const fetchUdfsForEntity = async (entityCode: string) => {
    setLoadingUdfs(true);
    try {
      setUdfCategories(await withTimeout(
        fetchTemplateUdfCategories(entityCode, _authToken, _identityProvider),
        CONFIGURATION_LOAD_TIMEOUT_MS,
        "Timed out loading UDFs."
      ));
    } catch (error) {
      console.error("Error fetching UDFs:", error);
      setOperationError(getErrorMessage(error, ErrorMessages.LoadUdfsFailed));
      setUdfCategories([]);
    } finally {
      setLoadingUdfs(false);
    }
  };

  const handleAddFilter = (filterKey: string) => {
    const def = availableFilters.find((f) => f.key === filterKey);
    if (!def || selectedFilters.find((r) => r.key === filterKey)) return;
    setSelectedFilters((prev) => [
      ...prev,
      mapFilterDefinitionToSelectedRow(def),
    ]);
    setShowFilterPicker(false);
    setHasGeneratedWorkbook(false);
  };

  const handleResetFilters = () => {
    setSelectedFilters(
      availableFilters
        .filter((def) => !def.generatedFromColumn)
        .map((def) => mapFilterDefinitionToSelectedRow(def, def.cascadeParents.length > 0 ? def.cascadeParents[0] : null))
    );
    setShowFilterPicker(false);
    setHasGeneratedWorkbook(false);
  };

  const handleRemoveFilter = (key: string) => {
    setSelectedFilters((prev) => prev.filter((r) => r.key !== key));
    setHasGeneratedWorkbook(false);
  };

  const handleFilterDrop = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    setSelectedFilters(reorderRows(selectedFilters, fromIndex, toIndex));
    setHasGeneratedWorkbook(false);
  };

  const handleFilterTypeChange = (key: string, type: string) => {
    setSelectedFilters((prev) => prev.map((r) => (r.key === key ? { ...r, type } : r)));
    setHasGeneratedWorkbook(false);
  };

  const handleFilterDisplayNameChange = (key: string, displayName: string) => {
    setSelectedFilters((prev) => prev.map((r) => (r.key === key ? { ...r, displayName } : r)));
    setHasGeneratedWorkbook(false);
  };

  const handleFilterRequiredChange = (key: string, required: boolean) => {
    setSelectedFilters((prev) => prev.map((r) => (r.key === key ? { ...r, required } : r)));
    setHasGeneratedWorkbook(false);
  };

  const handleFilterCascadeChange = (key: string, cascadeFrom: string | null) => {
    setSelectedFilters((prev) => prev.map((r) => (r.key === key ? { ...r, cascadeFrom } : r)));
    setHasGeneratedWorkbook(false);
  };

  const handleAddColumn = (colKey: string) => {
    const def = availableColumns.find((c) => c.key === colKey);
    if (!def || selectedColumns.find((r) => r.key === colKey)) return;
    setSelectedColumns((prev) => [...prev, mapColumnDefinitionToSelectedRow(def)]);
    setShowColumnPicker(false);
    setHasGeneratedWorkbook(false);
  };

  const handleResetColumns = () => {
    setSelectedColumns(
      availableColumns.map(mapColumnDefinitionToSelectedRow)
    );
    setShowColumnPicker(false);
    setHasGeneratedWorkbook(false);
  };

  const handleRemoveColumn = (key: string) => {
    setSelectedColumns((prev) => prev.filter((r) => r.key !== key));
    setHasGeneratedWorkbook(false);
  };

  const handleColumnDisplayNameChange = (key: string, newDisplayName: string) => {
    setSelectedColumns((prev) => prev.map((c) => (c.key === key ? { ...c, displayName: newDisplayName } : c)));
    setHasGeneratedWorkbook(false);
  };

  const handleColumnExcelFormatChange = (key: string, excelFormat: string) => {
    setSelectedColumns((prev) => prev.map((c) => (c.key === key ? { ...c, excelFormat } : c)));
    setHasGeneratedWorkbook(false);
  };

  const handleColumnDrop = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    setSelectedColumns(reorderRows(selectedColumns, fromIndex, toIndex));
    setHasGeneratedWorkbook(false);
  };

  const handleUdfCategoryToggle = (categoryName: string, isSelected: boolean) => {
    const newSelectedUdfs = handleUdfCategorySelect(categoryName, isSelected, udfCategories, selectedUdfs);
    setSelectedUdfs(newSelectedUdfs);
    setHasGeneratedWorkbook(false);
  };

  const handleUdfItemToggle = (itemId: string, isSelected: boolean) => {
    const newSelectedUdfs = handleUdfItemSelect(itemId, isSelected, selectedUdfs);
    setSelectedUdfs(newSelectedUdfs);
    setHasGeneratedWorkbook(false);
  };

  const handleMasterUdfToggle = (isSelected: boolean) => {
    const newSelectedUdfs = handleMasterUdfSelect(isSelected, udfCategories);
    setSelectedUdfs(newSelectedUdfs);
    setHasGeneratedWorkbook(false);
  };

  const handleGenerateWorkbook = async () => {
    setOperationError("");
    setOperationNotice("");
    setIsGenerating(true);
    try {
      const config = buildTemplateWorkbookConfig({
        templateName,
        selectedEntityType,
        entityNames,
        selectedFilters,
        selectedColumns,
        includeUdfs,
        selectedUdfs,
        hasUdfs: udfCategories.length > 0,
        dataReferenceMode: activeEmptyVisibleSheetName ? dataReferenceMode : "none",
      });
      const generationNotice = await generateTemplateWorkbook(config);
      if (generationNotice) {
        setOperationNotice(generationNotice);
      }
      setHasGeneratedWorkbook(true);
      await refreshActiveVisibleSheetState();
    } catch (err) {
      const officeErr = err as {
        message?: string;
        code?: string;
        debugInfo?: { errorLocation?: string; statement?: string };
      };
      const details = [
        officeErr.code,
        officeErr.debugInfo?.errorLocation,
        officeErr.debugInfo?.statement,
      ].filter(Boolean).join(" | ");
      const message = officeErr.message || String(err);
      setOperationError(`Generate failed: ${message}${details ? ` (${details})` : ""}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClearAll = (clearTemplateAssociation = true) => {
    setSelectedFilters([]);
    setSelectedColumns([]);
    setTemplateName("");
    setSelectedEntityType("");
    setHasGeneratedWorkbook(false);
    setOperationNotice("");
    setShowFilterPicker(false);
    setShowColumnPicker(false);
    setIncludeUdfs(false);
    setSelectedRoles([]);
    setWorkbookDetected(false);
    setDataReferenceMode("standardTable");
    if (clearTemplateAssociation) {
      setExistingTemplateId("");
      api.setTemplateId("");
    }
  };

  const executeSaveTemplate = async (updateTemplateId?: string | null) => {
    setOperationError("");
    setIsSaving(true);
    try {
      const config = buildTemplateWorkbookConfig({
        templateName,
        selectedEntityType,
        entityNames,
        selectedFilters,
        selectedColumns,
        includeUdfs,
        selectedUdfs,
        hasUdfs: udfCategories.length > 0,
      });
      const result = await saveTemplateToServer(config, selectedRoles, _authToken, _identityProvider, updateTemplateId);
      if (!result.success) {
        // result.error already carries BuilderMetrix's own message for the failure.
        setOperationError(result.error ?? ErrorMessages.TemplateSaveFailed);
        return;
      }
      const savedTemplateId = result.templateId || updateTemplateId || "";
      if (savedTemplateId) {
        await writeWorkbookTemplateId(savedTemplateId);
        setExistingTemplateId(savedTemplateId);
        api.setTemplateId(savedTemplateId);
      }
      handleClearAll(false);
      onSaveSuccess?.();
    } catch (err) {
      setOperationError(getErrorMessage(err, ErrorMessages.TemplateSaveFailed));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveTemplate = async () => {
    setOperationError("");
    if (getCurrentTemplateId()) {
      setIsSaveModeDialogOpen(true);
      return;
    }

    await executeSaveTemplate(null);
  };

  const handleOverwriteExistingTemplate = async () => {
    const templateId = getCurrentTemplateId();
    setIsSaveModeDialogOpen(false);
    await executeSaveTemplate(templateId);
  };

  const handleCancelSaveModeDialog = () => {
    setIsSaveModeDialogOpen(false);
  };

  return (
    <>
    <TemplateTabView
      isLoadingConfiguration={isLoadingConfiguration}
      workbookDetected={workbookDetected}
      operationError={operationError}
      operationNotice={operationNotice}
      isWorkbookBannerDismissed={isWorkbookBannerDismissed}
      setIsWorkbookBannerDismissed={setIsWorkbookBannerDismissed}
      selectedEntityType={selectedEntityType}
      entityTypeQuery={entityTypeQuery}
      setEntityTypeQuery={setEntityTypeQuery}
      entityNames={entityNames}
      loadingEntityNames={loadingEntityNames}
      loadingFilters={loadingFilters}
      loadingColumns={loadingColumns}
      loadingRoles={loadingRoles}
      selectedFilters={selectedFilters}
      availableFilters={availableFilters}
      showFilterPicker={showFilterPicker}
      setShowFilterPicker={setShowFilterPicker}
      selectedColumns={selectedColumns}
      availableColumns={availableColumns}
      showColumnPicker={showColumnPicker}
      setShowColumnPicker={setShowColumnPicker}
      udfCategories={udfCategories}
      selectedUdfs={selectedUdfs}
      includeUdfs={includeUdfs}
      setIncludeUdfs={setIncludeUdfs}
      templateName={templateName}
      setTemplateName={handleTemplateNameChange}
      activeEmptyVisibleSheetName={activeEmptyVisibleSheetName}
      dataReferenceMode={dataReferenceMode}
      setDataReferenceMode={setDataReferenceMode}
      roles={roles}
      selectedRoles={selectedRoles}
      setSelectedRoles={setSelectedRoles}
      isGenerating={isGenerating}
      isSaving={isSaving}
      hasGeneratedWorkbook={hasGeneratedWorkbook}
      handleClearAll={handleClearAll}
      handleEntityTypeChange={handleEntityTypeChange}
      handleAddFilter={handleAddFilter}
      handleResetFilters={handleResetFilters}
      handleRemoveFilter={handleRemoveFilter}
      handleFilterDrop={handleFilterDrop}
      handleFilterTypeChange={handleFilterTypeChange}
      handleFilterDisplayNameChange={handleFilterDisplayNameChange}
      handleFilterRequiredChange={handleFilterRequiredChange}
      handleFilterCascadeChange={handleFilterCascadeChange}
      handleAddColumn={handleAddColumn}
      handleResetColumns={handleResetColumns}
      handleRemoveColumn={handleRemoveColumn}
      handleColumnDisplayNameChange={handleColumnDisplayNameChange}
      handleColumnExcelFormatChange={handleColumnExcelFormatChange}
      handleColumnDrop={handleColumnDrop}
      handleUdfCategoryToggle={handleUdfCategoryToggle}
      handleUdfItemToggle={handleUdfItemToggle}
      handleMasterUdfToggle={handleMasterUdfToggle}
      handleGenerateWorkbook={handleGenerateWorkbook}
      handleSaveTemplate={handleSaveTemplate}
      setHasGeneratedWorkbook={setHasGeneratedWorkbook}
    />
    <ConfirmationForm
      isOpen={isSaveModeDialogOpen}
      onConfirm={handleOverwriteExistingTemplate}
      onCancel={handleCancelSaveModeDialog}
      dialogTitle="Save template"
      dialogContent="This workbook is associated with an existing template. Overwrite it?"
      confirmButtonLabel="Overwrite"
      cancelButtonLabel="Cancel"
      isConfirming={isSaving}
      confirmButtonDisabled={isSaving}
      cancelButtonDisabled={isSaving}
    />
    </>
  );
};

export default TemplateTab;

